import { differenceWith, filter, flatten, groupBy, uniq } from 'lodash';

import { Animation } from '@repo/database-core';
import { DatabaseWearableWithOwner, Item } from '@repo/types-client';

/**
 * Checks if an animation is on an override layer.
 *
 * Override layers are identified by having a layer number divisible by 10.
 * Animations on override layers take precedence over regular animations.
 *
 * @param {Animation} a - The animation to check
 * @returns {boolean} True if the animation is on an override layer
 */
const isOnOverrideLayer = (a: Animation): boolean => a.layer % 10 === 0;

/**
 * Checks if a wearable contains any animations on override layers.
 *
 * A wearable is considered an override wearable if any of its animations
 * are on override layers (layer numbers divisible by 10).
 *
 * @param {DatabaseWearableWithOwner} wearable - The wearable to check
 * @returns {boolean} True if the wearable contains any override animations
 */
const isOverrideWearable = (wearable: DatabaseWearableWithOwner): boolean =>
  wearable.animations.some(isOnOverrideLayer);

/**
 * Determines if a wearable should cause hair to be pinched.
 *
 * Hair pinching is applied when:
 * 1. The wearable is positioned on the HEAD, and
 * 2. Either it has an animation on layer 68 (specific hat layer) or it's an override wearable
 *
 * @param {DatabaseWearableWithOwner} wearable - The wearable to check
 * @returns {boolean} True if the wearable should cause hair pinching
 */
export const shouldPinch = (wearable: DatabaseWearableWithOwner): boolean =>
  wearable.platformPosition === 'HEAD' &&
  (wearable.animations.some((a) => a.layer === 68) ||
    isOverrideWearable(wearable));

/**
 * Determines if a doodle's hair should be pinched based on equipped wearables.
 *
 * This function checks all equipped wearables to see if any of them
 * would cause hair pinching according to the shouldPinch criteria.
 *
 * @param {DatabaseWearableWithOwner[]} equippedWearables - Array of currently equipped wearables
 * @returns {boolean} True if any equipped wearable should cause hair pinching
 */
export const doodleHairShouldPinch = (
  equippedWearables: DatabaseWearableWithOwner[],
) =>
  equippedWearables.some(
    (w) =>
      w.platformPosition === 'HEAD' &&
      (w.animations.some((a) => a.layer === 68) || isOverrideWearable(w)),
  );

/**
 * Checks if an equipped animation should be preserved when a new animation is added.
 *
 * An animation should be preserved if:
 * 1. For wearable animations: they have different body positions or layers
 * 2. For other animations: they have different types
 *
 * @param {Animation} equippedAnim - Currently equipped animation
 * @param {Animation} incomingAnim - New animation being added
 * @returns {boolean} True if the animations conflict and the equipped one should be replaced
 */
const shouldPreserveAnimation = (
  equippedAnim: Animation,
  incomingAnim: Animation,
): boolean => {
  if (equippedAnim.wearableId) {
    // For wearable animations, check body position and layer
    return (
      equippedAnim.bodyPosition === incomingAnim.bodyPosition &&
      equippedAnim.layer === incomingAnim.layer
    );
  }
  // For other animations, check type
  return equippedAnim.type === incomingAnim.type;
};

/**
 * Creates a list of all incoming animations for comparison with equipped animations.
 *
 * This function duplicates the new animations for each equipped animation to
 * facilitate comparison in the differenceWith function.
 *
 * @param {Animation[]} equippedAnims - Currently equipped animations
 * @param {Animation[]} newAnimations - New animations being added
 * @returns {Animation[]} Expanded list of new animations for comparison
 */
const getAllIncomingAnims = (
  equippedAnims: Animation[],
  newAnimations: Animation[],
): Animation[] => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return equippedAnims.reduce<Animation[]>((acc, animation) => {
    return [...acc, ...newAnimations];
  }, []);
};

/**
 * Determines which animations should remain equipped after adding new animations.
 *
 * This function:
 * 1. Identifies animations that should be preserved based on comparison rules
 * 2. Filters out animations that belong to wearables being replaced
 *
 * @param {Animation[]} equippedAnimations - Currently equipped animations
 * @param {Animation[]} animationsToEquip - New animations being added
 * @returns {Animation[]} List of animations that should remain equipped
 */
export const getAnimationsToEquip = (
  equippedAnimations: Animation[],
  animationsToEquip: Animation[],
): Animation[] => {
  // Track IDs of wearables whose animations should be preserved
  const animationsToPreserveIds: string[] = [];

  // Find animations that don't conflict with any incoming animations
  const animationsToPreserve = differenceWith(
    equippedAnimations,
    getAllIncomingAnims(equippedAnimations, animationsToEquip),
    (currentlyEquipped: Animation, incomingItem: Animation) => {
      if (shouldPreserveAnimation(currentlyEquipped, incomingItem)) {
        // If animations conflict, mark the wearable ID for preservation
        if (currentlyEquipped.wearableId !== null) {
          animationsToPreserveIds.push(currentlyEquipped.wearableId);
        }
        return true;
      }
      return false;
    },
  );

  // Filter out animations from wearables that are being replaced
  return filter(animationsToPreserve, (anim: Animation) => {
    // Ensure wearableId is not null before includes check
    return (
      anim.wearableId !== null &&
      !animationsToPreserveIds.includes(anim.wearableId)
    );
  });
};

/**
 * Groups equipped animations by their type for easier management.
 *
 * This function organizes animations into categories:
 * - Body animations (base character)
 * - Wearable animations (clothing, accessories)
 * - Hair animations
 * - Expression animations (facial expressions)
 *
 * @param {Animation[]} equippedAnimations - All currently equipped animations
 * @returns {Record<string, Animation[]>} Object with animations grouped by type
 */
export const getEquippedAnimationsByType = (
  equippedAnimations: Animation[],
): Record<string, Animation[]> => {
  const groupedAnimations = groupBy(equippedAnimations, 'type');
  return {
    equippedBodyAnimations: groupedAnimations['BODY'] || [],
    equippedWearableAnimations: groupedAnimations['WEARABLE'] || [],
    equippedHairAnimations: groupedAnimations['HAIR'] || [],
    equippedExpressionAnimations: groupedAnimations['EXPRESSION'] || [],
  };
};

/**
 * Removes animations that would conflict with new wearable animations.
 *
 * This function:
 * 1. Identifies animations at the same position or layer as new animations
 * 2. Handles override animations (which take precedence)
 * 3. Filters out animations from wearables that should be replaced
 *
 * @param {Object} params - Parameters object
 * @param {Animation[]} params.allEquippedWearableAnimations - All currently equipped wearable animations
 * @param {Animation[]} params.newWearableAnimations - New wearable animations being added
 * @returns {Animation[]} List of wearable animations that should remain equipped
 */
export const removeAnimationsAtPosition = ({
  allEquippedWearableAnimations,
  newWearableAnimations,
}: {
  allEquippedWearableAnimations: Animation[];
  newWearableAnimations: Animation[];
}): Animation[] => {
  // Track IDs of wearables that should be completely removed
  const wearableIdsToRemove: string[] = [];

  // Filter out animations that conflict with new animations
  const wearableAnimationsToPreserve = allEquippedWearableAnimations.filter(
    (equippedWearableAnim) => {
      // Extract unique positions and layers from new animations
      const newWearablePositions = uniq(
        newWearableAnimations.map((newAnim) => newAnim.position),
      );
      const newWearableLayers = uniq(
        newWearableAnimations.map((newAnim) => newAnim.layer),
      );

      // Check if any new animation is an override or if the equipped animation is an override
      const newAnimIsOverride = newWearableAnimations.some(isOnOverrideLayer);
      const overrideIsEquipped = isOnOverrideLayer(equippedWearableAnim);

      // If positions conflict, check for overrides and layer conflicts
      if (newWearablePositions.includes(equippedWearableAnim.position)) {
        // If either animation is an override, the equipped animation should be removed
        if (newAnimIsOverride || overrideIsEquipped) {
          return false;
        }

        // If layers conflict, mark the wearable for removal
        if (newWearableLayers.includes(equippedWearableAnim.layer)) {
          if (equippedWearableAnim.wearableId) {
            wearableIdsToRemove.push(equippedWearableAnim.wearableId);
          }
          return false;
        }
      }

      // No conflict, preserve the animation
      return true;
    },
  );

  // Filter out animations from wearables that should be completely removed
  return wearableAnimationsToPreserve.filter((anim) => {
    // Ensure wearableId is not null before includes check
    return (
      anim.wearableId !== null && !wearableIdsToRemove.includes(anim.wearableId)
    );
  });
};

/**
 * Adds a new wearable to the equipped wearables list, handling conflicts.
 *
 * This function:
 * 1. Removes any existing instance of the same wearable
 * 2. Determines which animations conflict and should be replaced
 * 3. Creates a new list of wearables based on the animations that should remain
 *
 * @param {DatabaseWearableWithOwner[]} equippedWearables - Currently equipped wearables
 * @param {DatabaseWearableWithOwner} newWearable - New wearable to equip
 * @returns {DatabaseWearableWithOwner[]} Updated list of equipped wearables
 */
export const addEquippedWearable = (
  equippedWearables: DatabaseWearableWithOwner[],
  newWearable: DatabaseWearableWithOwner,
): DatabaseWearableWithOwner[] => {
  // Remove any existing instance of the same wearable
  const cleanedWearables = equippedWearables.filter(
    (item) => item.id !== newWearable.id,
  );

  // Combine all wearables for processing
  const allKnownWearables = [...cleanedWearables, newWearable];
  const newWearableAnimations = newWearable.animations;

  // Get all animations from currently equipped wearables
  const allEquippedWearableAnimations = flatten(
    equippedWearables.map((w) => w.animations),
  );

  // Determine which animations should remain equipped
  const animationsToEquip = [
    ...removeAnimationsAtPosition({
      allEquippedWearableAnimations,
      newWearableAnimations,
    }),
    ...newWearableAnimations,
  ];

  // Get unique wearable IDs from the animations to equip
  const wearableIdsToEquip = uniq(animationsToEquip.map((a) => a.wearableId));

  // Filter wearables based on the animations that should remain
  const finalEquippedWearables = allKnownWearables.filter((w) =>
    wearableIdsToEquip.includes(w.id),
  );

  return finalEquippedWearables;
};

/**
 * Adds multiple new wearables to the equipped wearables list.
 *
 * This function sequentially adds each new wearable, handling conflicts
 * at each step to ensure proper layering and replacement.
 *
 * @param {DatabaseWearableWithOwner[]} equippedWearables - Currently equipped wearables
 * @param {DatabaseWearableWithOwner[]} newWearables - New wearables to equip
 * @returns {DatabaseWearableWithOwner[]} Updated list of equipped wearables
 */
export const addEquippedWearables = (
  equippedWearables: DatabaseWearableWithOwner[],
  newWearables: DatabaseWearableWithOwner[],
): DatabaseWearableWithOwner[] => {
  return newWearables.reduce(
    (acc, wearable) => addEquippedWearable(acc, wearable),
    equippedWearables,
  );
};

/**
 * Removes a wearable from the equipped wearables list.
 *
 * @param {DatabaseWearableWithOwner[]} equippedWearables - Currently equipped wearables
 * @param {DatabaseWearableWithOwner} deselectedWearable - Wearable to remove
 * @returns {DatabaseWearableWithOwner[]} Updated list of equipped wearables
 */
export const removeEquippedWearable = (
  equippedWearables: DatabaseWearableWithOwner[],
  deselectedWearable: DatabaseWearableWithOwner,
): DatabaseWearableWithOwner[] => {
  return equippedWearables.filter(
    (equippedWearable) =>
      getItemId(equippedWearable) !== getItemId(deselectedWearable),
  );
};

/**
 * Maps a trait value to its corresponding key in a mapping object.
 *
 * This function is useful for converting between different representations
 * of the same trait (e.g., between display names and internal IDs).
 *
 * @param {string} traitValue - The trait value to map
 * @param {Object} map - Object mapping keys to values
 * @returns {string} The key corresponding to the trait value, or '0' if not found
 */
export const mapTraitValueToKey = (
  traitValue: string,
  map: { [key: string]: string },
): string => {
  return (
    Object.entries(map).find(([, value]) => value === traitValue)?.[0] ?? '0'
  );
};

/**
 * Gets a consistent ID for an item, handling different item types.
 *
 * This function extracts either the tokenId (for NFTs) or the id (for other items)
 * to provide a consistent way to identify items across the application.
 *
 * @param {Item} item - The item to get an ID for
 * @returns {string} The item's ID as a string
 */
export const getItemId = (item: Item): string => {
  if ('tokenId' in item && item.tokenId) {
    return item.tokenId.toString();
  } else {
    return item.id.toString();
  }
};
